import React from 'react';
import { CheckCircle, Mail, Phone, ExternalLink } from 'lucide-react';
import FadeIn from '../components/FadeIn';
import CTASection from '../components/CTASection';
import { SEOHead, createBreadcrumbSchema } from '../components/SEOHead';

const SupportPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[color:var(--bg)] to-white">
      <SEOHead 
        title="Podpora | Školobot"
        description="Technická podpora Školobota v češtině. Webináře, školení, nápověda a přímá pomoc od našeho týmu."
        path="/podpora"
        schema={createBreadcrumbSchema([{ name: 'Úvod', path: '/' }, { name: 'Podpora', path: '/podpora' }])}
      />
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <h1 className="text-4xl md:text-5xl font-bold text-[color:var(--text-primary)] mb-4">
              Podpora
            </h1>
            <p className="text-lg text-[color:var(--text-secondary)]">
              Ne automat. Skutečná podpora.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <FadeIn delay={0.1}>
            <div className="bg-white rounded-3xl p-10 md:p-12 border-2 border-[color:var(--primary-100)] shadow-xl mb-12">
              <p className="text-xl text-[color:var(--text-secondary)] leading-relaxed mb-8">
                Když školy narazí na problém u běžných digitálních nástrojů, často končí u robotických odpovědí, cizího jazyka nebo nulové reakce. Se Školobotem je to jinak — když potřebujete pomoct, napíšete nám a ozve se člověk, který ví, co řešíte.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-[color:var(--primary-600)] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-[color:var(--text-primary)] mb-2">
                      Podpora v češtině od skutečných lidí
                    </h3>
                    <p className="text-sm text-[color:var(--text-secondary)]">
                      Když si nevíte rady, napíšete nám
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-[color:var(--primary-600)] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-[color:var(--text-primary)] mb-2">
                      Žádný anonymní helpdesk
                    </h3>
                    <p className="text-sm text-[color:var(--text-secondary)]">
                      Odpověď od člověka, ne od robota
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-[color:var(--primary-600)] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-[color:var(--text-primary)] mb-2">
                      Podpora, která zná školní praxi
                    </h3>
                    <p className="text-sm text-[color:var(--text-secondary)]">
                      Rozumíme tomu, co řešíte
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-[color:var(--primary-600)] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-[color:var(--text-primary)] mb-2">
                      Odpověď do 24 hodin
                    </h3>
                    <p className="text-sm text-[color:var(--text-secondary)]">
                      Vyřešíme váš problém rychle
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-[color:var(--border)]">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <a href="https://helpdesk.gavito.cz" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-[color:var(--primary-50)] border-2 border-[color:var(--primary-200)] rounded-xl p-4 hover:border-[color:var(--primary-400)] transition-all">
                    <ExternalLink className="w-5 h-5 text-[color:var(--primary-600)] flex-shrink-0" />
                    <div>
                      <div className="font-bold text-sm text-[color:var(--text-primary)]">Helpdesk</div>
                      <div className="text-xs text-[color:var(--text-secondary)]">helpdesk.gavito.cz</div>
                    </div>
                  </a>
                  <a href="tel:+420583033357" className="flex items-center gap-3 bg-gray-50 border-2 border-gray-200 rounded-xl p-4 hover:border-[color:var(--primary-400)] transition-all">
                    <Phone className="w-5 h-5 text-[color:var(--primary-600)] flex-shrink-0" />
                    <div>
                      <div className="font-bold text-sm text-[color:var(--text-primary)]">Telefon</div>
                      <div className="text-xs text-[color:var(--text-secondary)]">+420 583 033 357</div>
                    </div>
                  </a>
                  <a href="mailto:podpora@skolobot.cz" className="flex items-center gap-3 bg-gray-50 border-2 border-gray-200 rounded-xl p-4 hover:border-[color:var(--primary-400)] transition-all">
                    <Mail className="w-5 h-5 text-[color:var(--primary-600)] flex-shrink-0" />
                    <div>
                      <div className="font-bold text-sm text-[color:var(--text-primary)]">Email</div>
                      <div className="text-xs text-[color:var(--text-secondary)]">podpora@skolobot.cz</div>
                    </div>
                  </a>
                </div>
                <div className="text-center">
                  <a href="/#webinar">
                    <button className="bg-[color:var(--primary-500)] hover:bg-[color:var(--primary-600)] text-white px-8 py-3 rounded-xl font-semibold transition-colors">
                      Webinář zdarma
                    </button>
                  </a>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default SupportPage;